import {
  ArrowRight,
  ClipboardCheck,
  FileInput,
  NotebookPen,
  RotateCcw,
} from "lucide-react";
import { SectionLabel } from "@omnicanvas/ui";

const steps = [
  {
    icon: NotebookPen,
    label: "Capture where it is fastest",
    detail: "Apple Notes, Goodnotes, Preview, paper, or any tool already at hand.",
  },
  {
    icon: FileInput,
    label: "Clarify the useful context",
    detail: "Bring the relevant PDF, image, web clip, recording, or extracted text together around the idea.",
  },
  {
    icon: ClipboardCheck,
    label: "Close the loop",
    detail: "Confirm actions and open questions, then write the one sentence future you needs.",
  },
  {
    icon: RotateCcw,
    label: "Review and recover",
    detail: "Use a weekly queue to review, archive, or clean up—then retrieve through search, links, or the graph.",
  },
];

export function WorkflowBridge() {
  return (
    <section className="border-y border-slate-100 bg-slate-50 py-20 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:items-end">
          <div>
            <SectionLabel className="mb-4 text-indigo-600">A companion, not a monopoly</SectionLabel>
            <h2 className="text-3xl font-extrabold tracking-[-0.03em] text-slate-950 md:text-4xl">
              Use the app that fits the moment. Keep one system for what matters.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-7 text-slate-600">
              No single note app has to win every moment. Use a native app for
              fast handwriting or one-off markup, then move durable project
              context into a repeatable capture, clarify, act, and review loop.
            </p>
            <a
              href="/apple-notes-companion"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
            >
              See the Apple Notes companion workflow
              <ArrowRight size={16} />
            </a>
            <a
              href="/tools/ipad-note-app-finder"
              className="ml-4 mt-7 inline-flex text-sm font-semibold text-slate-500 hover:text-indigo-600"
            >
              Or compare iPad note workflows
            </a>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {steps.map(({ detail, icon: Icon, label }, index) => (
              <div
                key={label}
                className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <Icon size={19} />
                  </span>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                      {String(index + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-1 text-sm font-bold text-slate-900">{label}</h3>
                    <p className="mt-1.5 text-xs leading-5 text-slate-500">{detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-8 text-center text-xs font-medium text-slate-500">
          The core canvas, organization, and sync stay free. Paid plans fund
          ongoing transcription and AI compute.
        </p>
      </div>
    </section>
  );
}
